import * as profiling from '@ardoq/profiling';
import { logError } from '@ardoq/logging';
import {
  type TraversalAndGroupingResult,
  isGraphModelValid,
  getTraversalAndGroupingResult,
  getEmptyTraversalAndGroupingResult,
  BuildGraphForWorkspaceModeParams,
} from '@ardoq/graph';
import { getTraversalResultForWorkspaceMode } from './getTraversalResultForWorkspaceMode';

// Main util to build a specific graph for a view.
export const buildGraph: (
  params: BuildGraphForWorkspaceModeParams
) => TraversalAndGroupingResult = ({
  graphInterface,
  startSet,
  graph,
  traverseOptions = {
    maxDegreesIncoming: 0,
    maxDegreesOutgoing: 0,
    maxDegrees: 0,
    isParentRelationAsReference: false,
    outgoingReferenceTypes: null,
    incomingReferenceTypes: null,
    excludedComponentIds: new Set(),
  },
  groupingRules = [],
  viewId,
  sequentialTraverseOptions = [],
  includeOnlyConnectedComponents = false,
  useNewGrouping = false,
  i18nLocale,
  ctx,
}) => {
  const transaction = profiling.startTransaction(
    'buildGraph',
    500,
    profiling.Team.CORE
  );

  if (!isGraphModelValid(graphInterface, graph)) {
    // typically this means the graphModel$ has not updated itself with the latest state of the References collection... but a viewModel stream is rebuilding from some other update.
    logError(Error('Invalid graph model.'), null, {
      viewId,
      referenceCollectionSize: graphInterface.getReferenceCollectionSize(),
      referenceMapSize: graph.referenceMap.size,
    });
    return getEmptyTraversalAndGroupingResult();
  }

  const { componentIds, traversedReferenceIds, parentReferenceIds } =
    getTraversalResultForWorkspaceMode({
      graphInterface,
      startSet,
      graph,
      traverseOptions,
      sequentialTraverseOptions,
      includeOnlyConnectedComponents,
      ctx,
    });

  const traversalAndGroupingResult = getTraversalAndGroupingResult({
    graphInterface,
    graph,
    componentIds,
    traversedReferenceIds,
    parentReferenceIds,
    groupingRules,
    isParentRelationAsReference: traverseOptions.isParentRelationAsReference,
    useNewGrouping,
    i18nLocale,
  });

  const { maxDegrees, maxDegreesIncoming, maxDegreesOutgoing } =
    traverseOptions;
  profiling.endTransaction(transaction, {
    viewId,
    metadata: {
      groupCount: traversalAndGroupingResult.groupMap.size,
      maxDegrees,
      maxDegreesIncoming,
      maxDegreesOutgoing,
      startSetCount: startSet.length,
      referenceCount: graph.referenceMap.size,
      sourceCount: graph.sourceMap.size,
      targetCount: graph.targetMap.size,
    },
  });

  return traversalAndGroupingResult;
};
